import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { ApplicationService } from '../../../common/services/application.service';
import { StorageService } from '../../../common/services/storage.service';
import { CurrentUser } from '../../../common/models/user';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ApplicationActivity, CreateApplication, UpdateApplication } from '../../../common/models/application';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Guid } from 'guid-typescript';
import { BudgetPlanComponent } from './budget/budget.component';
import { ActivitiesComponent } from './activity/activities.component';
import { Association } from '../../../common/models/association';
import { AssociationService } from '../../../common/services/association.service';
import { FileUploadComponent } from '../../../common/components/file/file.component';
import { FormattedNumberInputDirective } from '../../../common/directives/inputs/formatted-number.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BudgetPlan } from '../../../common/models/submission';
import { catchError, EMPTY, finalize, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CostType, FundingSourceType } from '../../../common/models/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';

@Component({
    selector: 'app-application',
    templateUrl: './application.component.html',
    styleUrls: ['./application.component.scss'],
    imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    BudgetPlanComponent,
    ActivitiesComponent,
    FileUploadComponent,
    FormattedNumberInputDirective
]
})
export class ApplicationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };
  
  isHandset: boolean = false;

  isLoading = true;
  isUpdating = false;
  isSaving = false;
  
  applicationId!: string;
  grantId!: string;

  user: CurrentUser | null = null;

  generalInfoFormGroup: FormGroup;
  programProjectFormGroup: FormGroup;
  visibilityFormGroup: FormGroup;
  activitiesFormGroup: FormGroup;
  budgetPlanFormGroup: FormGroup;
  attachmentsFormGroup: FormGroup;

  constructor(
    private _formBuilder: FormBuilder,
    private associationService: AssociationService,
    private applicationService: ApplicationService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private location: Location
  ) {

    this.generalInfoFormGroup = this._formBuilder.group({
      organizationName: [null],
      address: [null],
      postalCodeAndHeadquarters: [null],
      county: [null],
      authorizedRepresentative: [null],
      authorizedRepresentativeEmail: [null],
      authorizedRepresentativeRole: [null],
      telephone: [null],
      mobilePhone: [null],
      fax: [null],
      organizationEmail: [null],
      website: [null],
      yearOfEstablishment: [null],
      dateOfRegistration: [null],
      registrationNumber: [null],
      registeredAuthority: [null],
      bankName: [null],
      bankAccountNumber: [null],
      oib: [null],
      rno: [null],
      objectivesAccordingToStatute: [null],
      purposeAndFieldOfOperation: [null],
      activitiesAccordingToStatute: [null],
      totalMembers: [null],
      totalIndividualMembers: [null],
      totalLegalEntities: [null],
      volunteerWorkShare: [null],
      numberOfVolunteers: [null],
      volunteerHoursInPreviousYear: [null],
      temporaryEmployees: [null],
      permanentEmployees: [null],
      isInVATSystem: [null],
      totalIncomeInPreviousYear: [null],
      incomeFromGovernmentDonationsInPreviousYear: [null],
      incomeFromLocalAndRegionalAuthoritiesInPreviousYear: [null],
      incomeFromInternationalOrganizationsInPreviousYear: [null],
      incomeFromBusinessesAndLegalEntitiesInPreviousYear: [null],
      incomeFromIndividualsInPreviousYear: [null],
      incomeFromNonprofitOrganizationsInPreviousYear: [null],
      membershipFeesInPreviousYear: [null],
      euFundsInPreviousYear: [null],
      totalSalaryPaidInPreviousYear: [null],
      totalCompensationPaidInPreviousYear: [null],
      ownSpaceSize: [null],
      rentedSpaceSize: [null],
      municipalSpaceSize: [null],
      monthlyRentForMunicipalSpace: [null],
      totalApprovedGrantsInPreviousYear: [null],
      numberOfPartnerships: [null],
      isAnnualReportPrepared: [null],
      reportRecipients: [null],
      implementsQualitySystem: [null],
      qualitySystemDetails: [null]
    });
  
    this.programProjectFormGroup = this._formBuilder.group({
      category: [null],
      area: [null],
      programProjectName: [null],
      summary: [null],
      expectedDurationMonths: [null],
      geographicalArea: [null],
      totalAmountRequired: [null],
      amountRequestedFromMunicipality: [null],
      additionalFundingSources: [null],
      motivationDescription: [null],
      projectGoals: [null],
      expectedResults: [null],
      contributionToGoals: [null],
      targetGroups: [null],
      endUsersImpact: [null],
      projectLeader: [null],
      numberOfVolunteersInvolved: [null],
      numberOfEmployeesInvolved: [null],
      similarExperience: [null]
    });
  
    this.visibilityFormGroup = this._formBuilder.group({
      publicEngagementPlan: [null]
    });

    this.activitiesFormGroup = this._formBuilder.group({
      activities: this._formBuilder.array([]),
    });

    this.budgetPlanFormGroup = this._formBuilder.group({
      id: [''],
      directCosts: this._formBuilder.array([]),
      indirectCosts: this._formBuilder.array([]),
      fundingSources: this._formBuilder.array([])
    });

    this.attachmentsFormGroup = this._formBuilder.group({
      fileStorageLocation: [`applications/${Guid.create().toString()}`]
    });
  }

  ngOnInit(): void {

    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      this.isHandset = result.matches;
    });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.grantId = params.get('grantId') || '';
      if (params.get('applicationId')) {
        this.applicationId = params.get('applicationId')!;
        this.isLoading = true;
        this.isUpdating = true;
        
         this.loadApplicationData$().pipe(
          finalize(() => this.isLoading = false),
          takeUntil(this.destroy$)
        ).subscribe();

      } else {
        this.initializeBudgetPlan();
        this.isLoading = false;
        this.isUpdating = false;
      }
    });


    this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;

      if (!this.isUpdating && this.user) {
        // We are in create mode and user is now available
  
        // Patch what we can directly from the user first
        this.patchFieldsFromUser();
  
        // If user has an association, fetch association details
        if (this.user.associationId) {
          this.associationService.getAssociation(this.user.associationId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (association) => {
              this.patchFieldsFromAssociation(association);
            },
            error: (err) => {
              console.error('Error fetching association data', err);
            }
          });
        }
      }
    });
  }

  ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
  }

  private initializeBudgetPlan(): void {
    this.populateBudgetPlanForm();
  }

  private populateActivitiesForm(activities?: ApplicationActivity[]): void {
      const fb = this._formBuilder;
      
      const activityControls = (activities ?? []).map(activity => fb.group({
        id: [activity.id ?? ''],
        name: [activity?.name || '', [Validators.required, Validators.maxLength(100)]],
        description: [activity?.description || '', [Validators.required, Validators.maxLength(1000)]],
        results: [activity?.results || '', [Validators.required, Validators.maxLength(1000)]],
        startDate: [activity?.startDate || null, Validators.required],
        endDate: [activity?.endDate || null, Validators.required]
      }));

    this.activitiesFormGroup.setControl('activities', fb.array(activityControls));
  }

  private populateBudgetPlanForm(budgetPlan?: BudgetPlan | null): void {
    const fb = this._formBuilder;

    const defaultFunding = [
      { id: '', type: FundingSourceType.OwnFunds, amount: 0 },
      { id: '', type: FundingSourceType.OtherPublicAuthorities, amount: 0 },
      { id: '', type: FundingSourceType.LocalAndRegionalAuthorities, amount: 0 },
      { id: '', type: FundingSourceType.Other, amount: 0 }
    ];

   const funding = (budgetPlan?.fundingSources?.length ? budgetPlan.fundingSources : defaultFunding).map(s => fb.group({
      id: [s.id ?? ''],
      type: [s.type, Validators.required],
      amount: [Number(s.amount ?? 0), [Validators.required, Validators.min(0)]]
    }));

    const items = budgetPlan?.budgetItems ?? [];

    const itemGroup = (i: any) => fb.group({
      id: [i.id ?? ''],
      type: [i.type, Validators.required],
      description: [i.description ?? '', [Validators.required, Validators.maxLength(500)]],
      amountRequested: [Number(i.amountRequested ?? 0), [Validators.required, Validators.min(0)]],
      totalAmount: [Number(i.totalAmount ?? 0), [Validators.required, Validators.min(0)]],
      explanation: [i.explanation ?? '', [Validators.required, Validators.maxLength(1000)]],
      fileStorageLocation: [i.fileStorageLocation || `budgetitems/${Guid.create().toString()}`]
    });

    const directCosts = items.filter(i => i.type === CostType.Direct).map(itemGroup);
    const indirectCosts = items.filter(i => i.type === CostType.Indirect).map(itemGroup);

    // Set arrays in one shot (avoids intermediate valueChanges noise)
    this.budgetPlanFormGroup.setControl('fundingSources', fb.array(funding));
    this.budgetPlanFormGroup.setControl('directCosts', fb.array(directCosts));
    this.budgetPlanFormGroup.setControl('indirectCosts', fb.array(indirectCosts));

    // Set id without emitting changes
    this.budgetPlanFormGroup.patchValue(
      { id: budgetPlan?.id ?? '' },
      { emitEvent: false }
    );
  }

  private patchFieldsFromUser(): void {
    if (!this.user) return;

    this.generalInfoFormGroup.patchValue({
      authorizedRepresentative: `${this.user.firstName} ${this.user.lastName}`,
      authorizedRepresentativeEmail: this.user.email,
      authorizedRepresentativeRole: this.user.title
    });
  }
  
  private patchFieldsFromAssociation(association: Association): void {
    // Patch association-related fields
    this.generalInfoFormGroup.patchValue({
      organizationName: association.name,
      address: association.address,
      postalCodeAndHeadquarters: association.postalCodeAndHeadquarters,
      county: association.county,
      organizationEmail: association.email,
      website: association.web,
      yearOfEstablishment: association.yearOfEstablishment,
      dateOfRegistration: association.dateOfRegistration,
      registrationNumber: association.registrationNumber,
      registeredAuthority: association.registeredAuthority,
      bankName: association.bankName,
      bankAccountNumber: association.bankAccountNumber,
      oib: association.oib,
      rno: association.rno,
      objectivesAccordingToStatute: association.objectivesAccordingToStatute,
      purposeAndFieldOfOperation: association.purposeAndFieldOfOperation,
      activitiesAccordingToStatute: association.activitiesAccordingToStatute,
      mobilePhone: association.contactNumber
    });
  }

  private loadApplicationData$(): Observable<any> {
    if (!this.applicationId) {
      return EMPTY;
    }

    return this.applicationService.getApplication(Guid.parse(this.applicationId)).pipe(
      tap(application => {
        this.generalInfoFormGroup.patchValue(application);
        this.programProjectFormGroup.patchValue(application);
        this.visibilityFormGroup.patchValue(application);
        this.attachmentsFormGroup.patchValue(application);
        this.populateActivitiesForm(application.activities);
        this.populateBudgetPlanForm(application.budgetPlan);
      }),
      catchError(error => {
        console.error('Error loading application:', error);
        return EMPTY;
      })
    );
  }

  private buildActivities(): ApplicationActivity[] {
    const raw = this.activitiesFormGroup.getRawValue() as any;
    
    return (raw?.activities ?? []).map((activity: any) => ({
      id: activity?.id ? Guid.parse(activity.id).toString() : Guid.EMPTY.toString(),
      name: activity?.name ?? '',
      description: activity?.description ?? '',
      startDate: activity?.startDate,
      endDate: activity?.endDate,
      results: activity?.results ?? ''
    }));
  }

  private buildBudgetPlan(): BudgetPlan {
      const raw = this.budgetPlanFormGroup.getRawValue() as any;

      const budgetItemsDirect = (raw?.directCosts ?? []).map((i: any) => ({
        id: i?.id ? Guid.parse(i.id).toString() : Guid.EMPTY.toString(),
        type: i?.type,
        description: i?.description ?? '',
        amountRequested: i?.amountRequested ?? 0,
        totalAmount: i?.totalAmount ?? 0,
        explanation: i?.explanation ?? '',
        fileStorageLocation: i?.fileStorageLocation
      }));

      const budgetItemsIndirect = (raw?.indirectCosts ?? []).map((i: any) => ({
        id: i?.id ? Guid.parse(i.id).toString() : Guid.EMPTY.toString(),
        type: i?.type,
        description: i?.description ?? '',
        amountRequested: i?.amountRequested ?? 0,
        totalAmount: i?.totalAmount ?? 0,
        explanation: i?.explanation ?? '',
        fileStorageLocation: i?.fileStorageLocation
      }));

      const fundingSources = (raw?.fundingSources ?? []).map((s: any) => ({
        id: s?.id ? Guid.parse(s.id).toString() : Guid.EMPTY.toString(),
        type: s?.type,
        amount: typeof s?.amount === 'number' ? s.amount : Number(s?.amount ?? 0)
      }));

      const bp = new BudgetPlan();
      bp.id = raw?.id ? Guid.parse(raw.id).toString() : Guid.EMPTY.toString();
      bp.budgetItems = [...budgetItemsDirect, ...budgetItemsIndirect];
      bp.fundingSources = fundingSources;
      return bp;
  }

  private shouldSyncFinancialFields(): boolean {
    try {
      const budgetPlan = BudgetPlan.fromFormGroup(this.budgetPlanFormGroup);
      const currentTotalRequired = this.programProjectFormGroup.get('totalAmountRequired')?.value || 0;
      const currentAmountRequested = this.programProjectFormGroup.get('amountRequestedFromMunicipality')?.value || 0;
      
      return Math.abs(budgetPlan.totalCostsActual - currentTotalRequired) > 0.01 ||
            Math.abs(budgetPlan.totalCostsRequested - currentAmountRequested) > 0.01;
    } catch {
      return false;
    }
  }

 private syncFinancialFieldsFromBudgetPlan(): void {
  try {
    const budgetPlan = BudgetPlan.fromFormGroup(this.budgetPlanFormGroup);
    
    this.programProjectFormGroup.patchValue({
      totalAmountRequired: budgetPlan.totalCostsActual,
      amountRequestedFromMunicipality: budgetPlan.totalCostsRequested
    }, { emitEvent: false });

  } catch (error) {
    console.warn('Financial fields snyc from budget plan failed:', error);
  }
}


onSubmit(stepper: MatStepper) {
    if (this.isUpdating) {
      this.updateApplication(stepper);
    } else {
      this.createApplication(stepper);
    }
  }

createApplication(stepper: MatStepper) {
  this.performSaveWithSync(() => this.performCreateApplication(stepper));
}

updateApplication(stepper: MatStepper) {
  this.performSaveWithSync(() => this.performUpdateApplication(stepper));
}

saveApplication() {
  this.performSaveWithSync(() => this.performSaveApplication());
}

private performSaveWithSync(saveAction: () => void): void {
    const needsSync = this.shouldSyncFinancialFields();
    
    if (!needsSync) {
      // No sync needed, save immediately
      saveAction();
      return;
    }
    
    // Sync is mandatory - do it first
    this.syncFinancialFieldsFromBudgetPlan();
    
    // Show informational dialog
    this.dialog.open(GenericDialogComponent, {
      data: {
        title: 'Financijski podaci ažurirani',
        message: `Sustav je iz vašeg proračuna (obrazac B2) automatski ažurirao polja.

  Na koraku "II. Podaci o programu/projektu", ažurirana su polja:

  - "Ukupan iznos potreban za provedbu"
  - "Iznos koji se traži od Općine"`,
        confirmButtonText: 'Razumijem',
        // No cancel button - only acknowledgment
      }
    }).afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Always save after user acknowledges
      saveAction();
    });
  }

  private performCreateApplication(stepper: MatStepper): void {
    if (!this.grantId) {
      console.error('Grant ID is missing');
      return;
    }

    const createApplication: CreateApplication = {
      ...this.generalInfoFormGroup.value,
      ...this.programProjectFormGroup.value,
      ...this.visibilityFormGroup.value,
      ...this.attachmentsFormGroup.value,
      activities: this.buildActivities(),
      budgetPlan: this.buildBudgetPlan(),
      associationId: this.user?.associationId,
      grantId: this.grantId
    };

    this.applicationService.createApplication(createApplication).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Prijava na natječaj uspješno kreirana', 'Zatvori', this.snackbarConfig);
        stepper.reset();
        this.router.navigate(['association', 'applications']);
      },
      error: (error) => {
        this.snackBar.open('Greška prilikom kreiranja prijave na natječaj', 'Zatvori', this.snackbarConfig);
        console.error(error);
      }
    });
  }

  private performUpdateApplication(stepper: MatStepper): void {
    const updateApplication: UpdateApplication = {
      id: this.applicationId,
      ...this.generalInfoFormGroup.value,
      ...this.programProjectFormGroup.value,
      ...this.visibilityFormGroup.value,
      ...this.attachmentsFormGroup.value,
      activities: this.buildActivities(),
      budgetPlan: this.buildBudgetPlan()
    };

    this.applicationService.updateApplication(updateApplication).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Prijava na natječaj uspješno ažurirana', 'Zatvori', this.snackbarConfig);
        stepper.reset();
        this.router.navigate(['association', 'applications']);
      },
      error: (error) => {
        this.snackBar.open('Greška prilikom ažuriranja prijave na natječaj', 'Zatvori', this.snackbarConfig);
        console.error(error);
      }
    });
  }

  private performSaveApplication(): void {
    if (this.isSaving) {
       return;
    }

    this.isSaving = true;

    const formsToValidate = [
      this.generalInfoFormGroup,
      this.programProjectFormGroup,
      this.visibilityFormGroup,
      this.activitiesFormGroup,
      this.budgetPlanFormGroup,
      this.attachmentsFormGroup
    ];

    const invalidForms = formsToValidate.filter(form => form && form.invalid);
    
    if (invalidForms.length > 0) {
      // Mark all fields as touched to show validation errors
      formsToValidate.forEach(form => form?.markAllAsTouched());
      
      this.snackBar.open('Molimo ispravno ispuniti sva obavezna polja prije spremanja', 'Zatvori', this.snackbarConfig);
      this.isSaving = false;
      return;
    }

    if (this.isUpdating) {
      // Edit mode - call update endpoint
      const updateApplication: UpdateApplication = {
        id: this.applicationId,
        ...this.generalInfoFormGroup.value,
        ...this.programProjectFormGroup.value,
        ...this.visibilityFormGroup.value,
        ...this.attachmentsFormGroup.value,
        activities: this.buildActivities(),
        budgetPlan: this.buildBudgetPlan()
      };

       this.applicationService.updateApplication(updateApplication).pipe(
        switchMap(() => this.loadApplicationData$()),
        finalize(() => this.isSaving = false),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.snackBar.open('Prijava na natječaj uspješno ažurirana', 'Zatvori', this.snackbarConfig);
        },
        error: (error) => {
          this.snackBar.open('Greška prilikom spremanja promjena', 'Zatvori', this.snackbarConfig);
          console.error(error);
        }
      });

    } else {

      if (!this.grantId) {
        console.error('Grant ID is missing');
        return;
      }

      // Create mode - call create endpoint and transform to edit
      const createApplication: CreateApplication = {
        ...this.generalInfoFormGroup.value,
        ...this.programProjectFormGroup.value,
        ...this.visibilityFormGroup.value,
        ...this.attachmentsFormGroup.value,
        activities: this.buildActivities(),
        budgetPlan: this.buildBudgetPlan(),
        associationId: this.user?.associationId,
        grantId: this.grantId
      };

       this.applicationService.createApplication(createApplication).pipe(
        tap(applicationId => {
          // Transform to edit mode seamlessly
          this.applicationId = applicationId;
          this.isUpdating = true;
        }),
        switchMap(() => this.loadApplicationData$()),
        finalize(() => this.isSaving = false),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          // Update URL without navigation to reflect edit mode
          const newUrl = this.router.createUrlTree(['association', 'applications', this.applicationId, 'edit']);
          this.location.replaceState(newUrl.toString());

          this.snackBar.open('Prijava na natječaj uspješno kreirana', 'Zatvori', this.snackbarConfig);
        },
        error: (error) => {
          this.snackBar.open('Greška prilikom kreiranja prijave na natječaj', 'Zatvori', this.snackbarConfig);
          console.error(error);
        }
      });
    }
  }
}